/**
 * Controlador Global do Cart Drawer
 * Instância única que funciona em todos os templates
 */

// Sistema de debug limpo
const DEBUG = false;
function log(...args) {
}

// Função auxiliar para limpar títulos de variantes (remover hífens iniciais)
function cleanVariantTitle(title) {
  if (!title) return '';
  // Remove qualquer hífen inicial ou hífen+espaço, ex: "-XL" → "XL"
  return title.replace(/^-+\s*/, '').trim();
}

class CartDrawer {
  constructor() {
    this.isOpen = false;
    this.isInitialized = false;
    this.isTransitioning = false;
    this.cartData = null;

    log('Inicializando...');
    this.init();
  }

  init() {
    if (this.isInitialized) {
      log('Já inicializado, pulando...');
      return;
    }

    log('Configurando event listeners...');
    this.setupEventListeners();
    this.isInitialized = true;
    log('Inicialização completa');
  }

  setupEventListeners() {
    // Intercept ALL form submissions to /cart/add
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (!form || form.hasAttribute('data-no-drawer')) return;

      // Skip bundle kit forms - they handle their own submission
      if (form.classList.contains('bundle-kit-form') || form.hasAttribute('data-bundle-kit')) {
        return;
      }

      e.preventDefault();

      try {
        const formData = new FormData(form);

        const isSpecialOffer = window.isSpecialOffer;
        if (isSpecialOffer) {
          formData.append('properties[Special Offer]', 'by BJJ Fanatics');
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        const data = await response.json();

        // Open drawer after successful add
        if (typeof window.openCartDrawer === 'function') {
          window.openCartDrawer();
        } else {
          document.dispatchEvent(new CustomEvent('cart:open'));
        }

        // Fetch updated cart and dispatch event
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        document.dispatchEvent(new CustomEvent('cart:loaded', { detail: { cart } }));
      } catch (error) {
        console.error('CartDrawer: Error adding product to cart:', error);
        // Fallback to original form submission
        form.submit();
      }
    });

    // Listen for cart:updated events (from ATC buttons)
    document.addEventListener('cart:updated', (event) => {
      console.log('Cart updated');
      this.cartData = event.detail;
    });

    // Listen for cart:open events
    document.addEventListener('cart:open', () => {
      console.log('Cart open');
      this.open();
    });

    // Listen for cart:close events
    document.addEventListener('cart:close', () => {
      console.log('Cart close');
      this.close();
    });

    // Close drawer when clicking close button
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-close]') || e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        this.close();
      }
    });

    // Close drawer when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isOpen && e.target.matches('.DrawerOverlay')) {
        this.close();
      }
    });

    // Handle cart item quantity changes
    document.addEventListener('click', (e) => {
      if (e.target.matches('.js-cart__btn--plus')) {
        e.preventDefault();
        this.updateQuantity(e.target.name, parseInt(e.target.parentNode.querySelector('.input-qtd').value) + 1);
      }

      if (e.target.matches('.js-cart__btn--minus')) {
        e.preventDefault();
        const input = e.target.parentNode.querySelector('.input-qtd');
        const newQuantity = Math.max(0, parseInt(input.value) - 1);

        if (newQuantity === 1) {
          e.target.disabled = true;
        }

        this.updateQuantity(e.target.name, newQuantity);
      }

      if (e.target.matches('.js-cart__btn--remove')) {
        e.preventDefault();
        this.removeItem(e.target.name);
      }
    });

    // Handle quantity input changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('.input-qtd')) {
        const quantity = Math.max(1, parseInt(e.target.value) || 1);
        this.updateQuantity(e.target.dataset.variantId, quantity);
      }
    });
  }

  async open() {
    // Prevent opening if already open
    if (this.isOpen && !window.isHomeTwoProducts) {
      return;
    }

    // If in transition, wait a bit and try again
    if (this.isTransitioning) {
      setTimeout(() => this.open(), 100);
      return;
    }

    this.isTransitioning = true;

    // Ensure drawer exists
    this.ensureDrawerExists();

    // Add open classes
    document.body.classList.add('cart-drawer-open', 'cart-open');
    this.isOpen = true;

    // Disable background scroll
    /* document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden'; */

    // Load and render cart data
    await this.loadCartData();
    await this.renderCart();

    // Reset transition flag after animation
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300); // Reduced from 400ms
  }

  close() {
    // Prevent closing if already closed
    if (!this.isOpen) {
      return;
    }

    // If in transition, wait a bit and try again
    if (this.isTransitioning) {
      setTimeout(() => this.close(), 100);
      return;
    }

    this.isTransitioning = true;

    document.body.classList.remove('cart-drawer-open', 'cart-open');
    this.isOpen = false;

    // Re-enable background scroll
    /* document.body.style.overflow = '';
    document.documentElement.style.overflow = ''; */

    // Reset transition flag after animation
    setTimeout(() => {
      this.isTransitioning = false;
    }, 300); // Reduced from 400ms
  }

  ensureDrawerExists() {
    let cartDrawer = document.querySelector('.cart-drawer');
    if (!cartDrawer) {
      // Create drawer HTML
      const drawerHTML = `
        <div id="CartDrawer" class="cart-drawer drawer--right" tabindex="-1" data-section-type="cart-drawer">
          <div class="cart-drawer__header">
            <div class="cart-drawer__title">
              <span class="drawer-title">Your Cart</span>
            </div>
            <button class="drawer-close" aria-label="Close cart" data-cart-close>
              <span>×</span>
            </button>
          </div>
          <div class="cart-containers">
            <div class="cart-top">
              <div class="cart-drawer__shipping">
                <div class="cart-drawer__shipping-bar">
                  <div class="cart-drawer__shipping-fill"></div>
                </div>
              </div>
              <div class="cart-drawer__shipping-text shipping__message--discount">We Pay Shipping On Orders Over $100</div>
              <div class="product-content cart-products-wrapper">
                <form method="post" action="/checkout" class="cart-products-wrapper">
                  <ul class="list-products">
                    <li class="cart__empty text-center" data-cart-view="data-cart-view">
                      <p class="empty-cart">Your cart is empty</p>
                    </li>
                  </ul>
                </form>
              </div>
            </div>
            <div class="total-price">
              <span>Total: </span>
              <span class="jq-total-price price money">$0.00</span>
              <span id="cart-drawer-freeshipping-msg" class="free-shipping-message" style="display: none;">FREE SHIPPING!</span>
            </div>
            <div class="cart-drawer__footer">
              <a href="/checkout" class="btn btn--primary cart-drawer__checkout">Checkout</a>
            </div>
          </div>
        </div>
      `;

      document.body.insertAdjacentHTML('beforeend', drawerHTML);
    }
  }

  async loadCartData() {
    try {
      const response = await fetch('/cart.js');
      this.cartData = await response.json();

      // Update header counter
      const cartCount = document.querySelector('.header__cart__count');
      if (cartCount) {
        cartCount.textContent = this.cartData.item_count;
      }

      return this.cartData;
    } catch (error) {
      console.error('CartDrawer: Error loading cart data:', error);
      return null;
    }
  }

  async renderCart() {
    console.log('Rendering cart');
    if (!this.cartData || this.cartData.items == undefined) {
      return;
    }

    // Update total
    const cartTotal = document.querySelector('.cart-drawer .jq-total-price');
    if (cartTotal) {
      cartTotal.textContent = '$' + (this.cartData.total_price / 100).toFixed(2);
    }

    // Render items
    const cartItemsContainer = document.querySelector('.cart-drawer .list-products');
    if (cartItemsContainer) {
      let cartItemsHTML = '';
      const customProductPropertiesStr = 'Jacket base color,Jacket lapel color,Jacket stitching color,Pants base color,Pants stitching color,Back text,Back text font,Back text color,Back Design,Front Logo,Preview,Jacket Inside Design,Order Note,Back Torso Design,Front Torso Design,Long Sleeves Color,Short Sleeves Color,Sleeves Size,Torso Color,Belt Color,Belt Stripes,Black Belt Stripes,Select a Color for Your Name,Select a Font for Your Name,Size,Type Your Name'.toUpperCase().split(',')

      if (this.cartData.items.length > 0) {
        this.cartData.items.forEach((item, index) => {
          if (Object.keys(item.properties).some(property => customProductPropertiesStr.includes(property.toUpperCase()))) {
            window.cartContainsCustomProduct = true;
            const proceedButton = document.querySelector('#ProceedToCheckoutBottom');
            if (proceedButton) {
              proceedButton.type = 'button';
              proceedButton.addEventListener('click', function (e) {
                e.preventDefault();
                window.location.href = '/cart';
              });
            }
          }

          cartItemsHTML += `
            <li class="cart-product ${item.handle}" id="jq-cart-item-${item.id}" data-variant="${item.key}" data-line="${index + 1}">
              <div class="cart-product-image-wrapper">
                <a aria-label="ImgCartDrawer-${index}" href="${item.url}" id="ImgCartDrawer-${index}">
                  <img src="${item.image}&width=120" alt="${item.title}"/>
                </a>
              </div>
              <div class="cart-product-wrapper">
                <div class="product-info">
                  <p class="title-item-cart">
                    <a aria-label="${item.product_title}" href="${item.url}" class="title-item-cart">
                    ${item.properties["Pre-order"] ? "PRE-ORDER " + item.product_title.replace("Special Offer - ", "") : item.product_title.replace("Special Offer - ", "")}
                    </a>
                  </p>
                  ${item.variant_title ? '<small class="product-variant">' + cleanVariantTitle(item.variant_title) + '</small>' : ''}
                  <div class="cart-product-btn-wrapper">
                    <button type="button" aria-label="IconMinus-${index}" id="IconMinus-${index}" class="btn-minus-${item.variant_id} btn icon-minus btn-minus js-cart__btn--minus" name="${item.key}" data-line-index="${index + 1}" ${item.quantity === 1 ? 'disabled' : ''}></button>
                    <input aria-label="InputQtd-${index}" id="InputQtd-${index}" class="jq-input-qtd-${item.id} input-qtd" name="quantity" min="1" data-variant-id="${item.variant_id}" value="${item.quantity}" type="number">
                    <button type="button" aria-label="IconPlus-${index}" id="IconPlus-${index}" class="btn-plus-${item.variant_id} btn icon-plus btn-plus js-cart__btn--plus" name="${item.key}" data-line-index="${index + 1}"></button>
                  </div>
                </div>
                <div class="product-price">
                  <strong class="price money" id="jq-cart-item-price-${item.id}-${index}">$${(item.line_price / 100).toFixed(2)}</strong>
                  <button type="button" aria-label="remove-cart" id="bt-remove-cart-${index}" class="js-cart__btn--remove bt-remove-cart" name="${item.key}">Remove <span class="spinner__wrapper"><span class="spinner"></span></span></button>
                </div>
              </div>
            </li>
          `;
        });
      } else if (this.cartData.item_count === 0) {
        cartItemsHTML = '<li class="cart__empty text-center" data-cart-view="data-cart-view"><p class="empty-cart">Your cart is empty</p></li>';
      }

      cartItemsContainer.innerHTML = cartItemsHTML;
    }
    // Update messages
    this.updateCartMessages();

    await new Promise(resolve => setTimeout(resolve, 200));
  }

  updateCartMessages() {
    if (!this.cartData) return;

    // Update drawer title
    const drawerTitle = document.querySelector('.cart-drawer .drawer-title');
    if (drawerTitle) {
      if (this.cartData.item_count > 0) {
        drawerTitle.innerHTML = '<span class="drawer-title">✓ Your Cart</span>';
      } else {
        drawerTitle.innerHTML = '<span class="drawer-title">Your Cart is Empty</span>';
      }
    }

    // Update shipping message
    const shippingMessage = document.querySelector('.cart-drawer .shipping__message--discount');
    if (shippingMessage) {
      const discountValue = 10000; // $100.00 in cents
      const remaining = discountValue - this.cartData.total_price;
      if (this.cartData.item_count === 0) {
        shippingMessage.innerHTML = 'We Pay Shipping On Orders Over $100';
      } else if (remaining > 0) {
        const remainingFormatted = '$' + (remaining / 100).toFixed(2);
        shippingMessage.innerHTML = `Add ${remainingFormatted} more to get FREE SHIPPING`;
      } else {
        shippingMessage.innerHTML = 'YES! YOU\'VE UNLOCKED FREE U.S. SHIPPING!';
      }
    }

    // Update shipping bar
    const shippingFill = document.querySelector('.cart-drawer .cart-drawer__shipping-fill');
    if (shippingFill) {
      const progress = Math.min(this.cartData.total_price, 10000) / 10000 * 100;
      shippingFill.style.width = progress + '%';
    }

    // Update free shipping message
    const freeShippingMsg = document.querySelector('#cart-drawer-freeshipping-msg');
    if (freeShippingMsg) {
      if (this.cartData.total_price >= 10000) {
        freeShippingMsg.innerHTML = 'FREE SHIPPING!';
        freeShippingMsg.style.display = 'block';
      } else {
        freeShippingMsg.style.display = 'none';
      }
    }
  }

  async updateQuantity(variantId, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      const data = await response.json();

      // Reload cart data and re-render
      await this.loadCartData();
      await this.renderCart();

      // Dispatch events for proceed button update
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.cartData } }));
      document.dispatchEvent(new CustomEvent('cart:loaded', { detail: { cart: this.cartData } }));

    } catch (error) {
      console.error('CartDrawer: Error updating quantity:', error);
    }
  }

  async removeItem(variantId) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 0
        })
      });

      const data = await response.json();

      // Reload cart data and re-render
      await this.loadCartData();
      await this.renderCart();

      // Dispatch events for proceed button update
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.cartData } }));
      document.dispatchEvent(new CustomEvent('cart:loaded', { detail: { cart: this.cartData } }));

    } catch (error) {
      console.error('CartDrawer: Error removing item:', error);
    }
  }
}

// Global initialization
function initCartDrawer() {
  if (window.CartDrawerInitialized) {
    return;
  }


  // Create global instance
  window.cartDrawer = new CartDrawer();

  // Expose global functions
  window.openCartDrawer = window.cartDrawer.open.bind(window.cartDrawer);
  window.closeCartDrawer = window.cartDrawer.close.bind(window.cartDrawer);

  // Rebuy widget integration - UPDATED FOR NEW CART DRAWER
  // Listen for Rebuy's "loaded" event to ensure the object is defined
  document.addEventListener('rebuy.loaded', () => {
    const rebuyWidgetContainer = document.querySelector('div[data-rebuy-id="177622"]');
    // console.log('Rebuy widget container found', rebuyWidgetContainer);
    if (rebuyWidgetContainer) {
      // console.log('Rebuy widget container found', rebuyWidgetContainer);
      rebuyWidgetContainer.classList.add('rebuy-widget-container');
    }
  });

  document.addEventListener('rebuy:cart.add', async (event) => {
    try {
      // Fetch fresh cart data
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        cache: 'no-cache'
      });
      const cart = await res.json();

      // Dispatch global events for consistency
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      document.dispatchEvent(new CustomEvent('cart:loaded', { detail: { cart } }));

      // Update cart drawer content
      if (window.cartDrawer && typeof window.cartDrawer.renderCart === 'function') {
        await window.cartDrawer.renderCart(cart);
      }

      // Open drawer
      if (typeof window.openCartDrawer === 'function') {
        window.openCartDrawer();
      } else {
        document.body.classList.add('cart-drawer-open', 'cart-open');
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    } catch (err) {
      console.error('CartDrawer: Error updating cart after Rebuy add:', err);
      // Fallback: try to open drawer anyway
      if (typeof window.openCartDrawer === 'function') {
        window.openCartDrawer();
      } else {
        document.body.classList.add('cart-drawer-open', 'cart-open');
      }
    }
  });

  // Global function to update product quantity display
  window.updateProductQuantityDisplay = window.isBundleKit ? undefined : function () {
    // Find all quantity inputs
    const quantityInputs = document.querySelectorAll('.quantity-input, input[name="quantity"], input[type="number"]');
    const addToCartBtns = document.querySelectorAll('.btn-add-tocart');

    if (quantityInputs.length > 0 && addToCartBtns.length > 0) {
      quantityInputs.forEach((input, index) => {
        const quantity = parseInt(input.value) || 1;

        addToCartBtns.forEach((btn, btnIndex) => {
          const btnItems = btn.querySelector('.btn-items');
          if (btnItems) {
            const itemText = '(' + quantity + ' ITEM' + (quantity > 1 ? 'S' : '') + ')';
            btnItems.textContent = itemText;
          }
        });
      });
    }
  };


  // Global function to update Proceed to Checkout button - ENHANCED WITH VALIDATION
  let proceedUpdateTimer;

  window.updateProceedButton = function (cart) {
    const proceedButton = document.querySelector('#ProceedToCheckoutBottom');

    if (!cart || !cart.item_count || cart.item_count < 0) {
      proceedButton.style.display = 'none';
      return;
    } else {
      document.querySelector('.cart-containers').classList.remove('empty');
    }

    clearTimeout(proceedUpdateTimer);
    proceedUpdateTimer = setTimeout(() => {
      const totalItems = cart.item_count;
      const label = totalItems === 1 ? 'Item' : 'Items';
      const text = `Proceed To Checkout (${totalItems} ${label}) →`;

      // Only update the item count, preserve original layout
      document.querySelectorAll('.jq_qtd_bt_proceed, .qtd_bt_proceed').forEach(btn => {
        if (!btn) return;
        // Update only the item count text, preserve HTML structure
        const itemText = `${totalItems} ${label}`;
        btn.textContent = itemText;
      });

      proceedButton.style.display = 'flex';
    }, 100); // debounce for overlapping events

    if (window.cartContainsCustomProduct) {
      proceedButton.type = 'button';
      proceedButton.addEventListener('click', function (e) {
        e.preventDefault();
        window.location.href = '/cart';
      });
    }
  };

  // Mark as initialized
  window.CartDrawerInitialized = true;

}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartDrawer);
} else {
  initCartDrawer();
}

// Re-initialize on section loads (for Shopify's fetch sections)
document.addEventListener('shopify:section:load', initCartDrawer);

// Global quantity change listeners
document.addEventListener('DOMContentLoaded', function () {
  // Listen for quantity changes on any input
  document.addEventListener('input', function (e) {
    if (e.target.matches('.quantity-input, input[name="quantity"], input[type="number"]')) {
      setTimeout(() => {
        if (window.updateProductQuantityDisplay && !window.isHomeTwoProducts) {
          window.updateProductQuantityDisplay();
        }
      }, 50);
    }
  });

  // Listen for quantity button clicks
  document.addEventListener('click', function (e) {
    if (e.target.matches('[data-quantity-minus], [data-quantity-plus]')) {
      setTimeout(() => {
        if (window.updateProductQuantityDisplay && !window.isHomeTwoProducts) {
          window.updateProductQuantityDisplay();
        }
      }, 100);
    }
  });

  // Initial update
  setTimeout(() => {
    if (window.updateProductQuantityDisplay && !window.isHomeTwoProducts) {
      window.updateProductQuantityDisplay();
    }
  }, 500);
});

// Proceed button update listeners - ENHANCED WITH VALIDATION
document.addEventListener('cart:updated', function (e) {
  const cart = e.detail?.cart;
  if (cart?.item_count >= 0 && window.updateProceedButton) {
    window.updateProceedButton(cart);
  }
});

document.addEventListener('cart:loaded', function (e) {
  const cart = e.detail?.cart;
  if (cart?.item_count >= 0 && window.updateProceedButton) {
    window.updateProceedButton(cart);
  }
});

document.addEventListener('cart:change', function (e) {
  const cart = e.detail?.cart;
  if (cart?.item_count >= 0 && window.updateProceedButton) {
    window.updateProceedButton(cart);
  }
});

// Update proceed button when drawer opens
document.addEventListener('cart:open', async function () {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    if (window.updateProceedButton) {
      window.updateProceedButton(cart);
    }
  } catch (err) {
    console.error('CartDrawer: Error loading cart for Proceed button:', err);
  }
});

// Listen for quantity changes inside the drawer
document.addEventListener('click', function (e) {
  if (e.target.matches('.cart__qty-plus, .cart__qty-minus, .js-cart__btn--plus, .js-cart__btn--minus')) {
    setTimeout(async () => {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        if (window.updateProceedButton) {
          window.updateProceedButton(cart);
        }
      } catch (err) {
        console.error('CartDrawer: Error fetching cart after quantity change:', err);
      }
    }, 200);
  }
});

document.addEventListener('change', function (e) {
  if (e.target.matches('input[name="quantity"], .input-qtd')) {
    setTimeout(async () => {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        if (window.updateProceedButton) {
          window.updateProceedButton(cart);
        }
      } catch (err) {
        console.error('CartDrawer: Error fetching cart after input change:', err);
      }
    }, 200);
  }
});

